import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BarChart3, Loader2, Lightbulb, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const eur = (v) => (v == null ? "—" : `€${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const pct = (v) => (v == null ? "—" : `${v}%`);

function MetricTable({ title, rows, lang, leadOk }) {
  const L = (it, en) => (lang === "en" ? en : it);
  if (!rows || rows.length === 0) return null;
  const bestRevpar = Math.max(...rows.map((r) => r.revpar || 0));
  return (
    <div className="rounded-xl border border-border overflow-hidden" data-testid={`rp-table-${title}`}>
      <div className="px-4 py-2.5 bg-muted/50 text-sm font-semibold">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
            {[L("Categoria", "Category"), "ADR", "RevPAR", L("Occup.", "Occ."), L("Ricavo", "Revenue"),
              L("Notti", "Nights"), L("Cancell.", "Cancel."), "Lead"].map((h, i) => (
              <th key={i} className={`px-3 py-2 font-semibold ${i === 0 ? "" : "text-right"}`}>{h}</th>))}
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const best = (r.revpar || 0) === bestRevpar && bestRevpar > 0;
              return (
                <tr key={r.key} className={`border-b border-border/60 last:border-0 ${best ? "bg-emerald-500/5" : ""}`} data-testid={`rp-row-${r.key}`}>
                  <td className="px-3 py-2 font-medium flex items-center gap-1.5">
                    {r.label}
                    {best && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">{L("miglior RevPAR", "top RevPAR")}</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-data">{eur(r.adr)}</td>
                  <td className="px-3 py-2 text-right font-data font-semibold">{eur(r.revpar)}</td>
                  <td className="px-3 py-2 text-right font-data">{pct(r.occupancy_pct)}</td>
                  <td className="px-3 py-2 text-right font-data">{eur(r.revenue)}</td>
                  <td className="px-3 py-2 text-right font-data">{r.room_nights}</td>
                  <td className="px-3 py-2 text-right font-data">{r.bookings + r.cancellations > 0 ? `${r.cancellation_rate}%` : "—"}</td>
                  <td className="px-3 py-2 text-right font-data">{leadOk && r.avg_lead_time != null ? `${r.avg_lead_time}gg` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function YoYBars({ yoy, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  if (!yoy) return null;
  const items = Object.entries(yoy.strategy || {})
    .map(([k, v]) => ({ k, ...v })).filter((x) => x.current > 0 || x.previous > 0)
    .sort((a, b) => b.current - a.current).slice(0, 6);
  if (items.length === 0) return null;
  return (
    <div className="rounded-xl border border-border p-4" data-testid="rp-yoy">
      <p className="text-sm font-semibold mb-3">{L(`Quota per strategia · quest'anno vs ${yoy.prev_year}`, `Strategy share · this year vs ${yoy.prev_year}`)}</p>
      <div className="space-y-2.5">
        {items.map((x) => (
          <div key={x.k} className="text-xs">
            <div className="flex justify-between mb-1">
              <span className="font-medium capitalize">{x.k.replace("_", " ")}</span>
              <span className="flex items-center gap-1">
                {x.current}% {x.delta !== 0 && (x.delta > 0
                  ? <span className="text-emerald-600 flex items-center"><TrendingUp className="w-3 h-3" />+{x.delta}</span>
                  : <span className="text-red-500 flex items-center"><TrendingDown className="w-3 h-3" />{x.delta}</span>)}
              </span>
            </div>
            <div className="flex gap-1 items-center">
              <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.min(100, x.current)}%` }} /></div>
              <span className="w-20 text-right text-muted-foreground">{yoy.prev_year}: {x.previous}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RatePerformance({ propId, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(null);

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/rate-performance${y ? `?year=${y}` : ""}`);
      setData(data); setYear(data.year);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [propId]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="bg-card border border-border rounded-lg p-6"><Loader2 className="w-5 h-5 animate-spin inline text-muted-foreground" /></div>;
  if (!data) return null;

  if (!data.has_data) {
    return (
      <div className="bg-card border border-border rounded-lg p-6" data-testid="rate-performance">
        <div className="flex items-center gap-2 mb-1"><BarChart3 className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Performance Tariffe", "Rate Performance")}</h2></div>
        <p className="text-sm text-muted-foreground mt-2" data-testid="rp-empty">{data.message}</p>
      </div>
    );
  }

  const t = data.totals || {};
  const chartData = (data.by_channel || []).filter((r) => r.revpar).slice(0, 6)
    .map((r) => ({ name: r.label, revpar: r.revpar, adr: r.adr }));
  const COLORS = ["#2563eb", "#0891b2", "#16a34a", "#d97706", "#7c3aed", "#db2777"];

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6 space-y-5" data-testid="rate-performance">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Performance Tariffe", "Rate Performance")}</h2></div>
        <select value={year || ""} onChange={(e) => load(Number(e.target.value))} data-testid="rp-year"
          className="h-9 rounded-md border border-border bg-background px-2 text-sm">
          {(data.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rp-totals">
        {[[L("ADR medio", "Avg ADR"), eur(t.adr)], ["RevPAR", eur(t.revpar)], [L("Occupazione", "Occupancy"), pct(t.occupancy_pct)],
          [L("Ricavo", "Revenue"), eur(t.revenue)], [L("Notti vendute", "Nights sold"), t.room_nights],
          [L("Cancellazioni", "Cancellations"), `${t.cancellations} (${t.cancellation_rate}%)`]].slice(0, 4).map(([k, v], i) => (
          <div key={i} className="rounded-lg border border-border p-3">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">{k}</p>
            <p className="text-xl font-bold mt-0.5">{v}</p>
          </div>
        ))}
      </div>

      {chartData.length > 1 && (
        <div className="rounded-xl border border-border p-4">
          <p className="text-sm font-semibold mb-2">{L("RevPAR per canale", "RevPAR by channel")}</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n) => [eur(v), n === "revpar" ? "RevPAR" : "ADR"]} />
              <Bar dataKey="revpar" radius={[4, 4, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <MetricTable title={L("Trattamento", "Meal plan")} rows={data.by_meal_plan} lang={lang} leadOk={data.lead_time_available} />
      <MetricTable title={L("Strategia tariffaria", "Rate strategy")} rows={data.by_strategy} lang={lang} leadOk={data.lead_time_available} />
      <MetricTable title={L("Canale", "Channel")} rows={data.by_channel} lang={lang} leadOk={data.lead_time_available} />

      {(data.tags || []).some((tg) => tg.bookings > 0) && (
        <div className="flex flex-wrap gap-2 items-center" data-testid="rp-tags">
          <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">{L("Tag tariffe", "Rate tags")}:</span>
          {data.tags.filter((tg) => tg.bookings > 0).map((tg) => (
            <span key={tg.key} className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium">
              {tg.label} · {tg.bookings} {L("pren.", "bk")} · ADR {eur(tg.adr)}
            </span>
          ))}
        </div>
      )}

      {data.yoy && <YoYBars yoy={data.yoy} lang={lang} />}

      {!data.lead_time_available && (
        <p className="text-xs text-muted-foreground" data-testid="rp-lead-note">{L("Lead time non disponibile: il file importato non contiene la data di prenotazione.", "Lead time unavailable: the imported file has no booking date.")}</p>
      )}

      {(data.insights || []).length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="rp-insights">
          <div className="flex items-center gap-2 mb-2"><Lightbulb className="w-4 h-4 text-amber-500" /><p className="text-sm font-bold">{L("Cosa ha imparato Revenue AI", "What Revenue AI learned")}</p></div>
          <ul className="space-y-1.5">
            {data.insights.map((s, i) => <li key={i} className="text-sm flex items-start gap-2"><span className="text-amber-500 mt-0.5">•</span>{s}</li>)}
          </ul>
        </div>
      )}
    </motion.div>
  );
}
